import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Icon from './Icon';
import Avatar from './Avatar';
import { useOrgBrand } from '../context/OrgBrandContext';
import { useModal } from '../context/ModalContext';
import '../styles/MeetingScheduler.css';
import CustomSelect from './CustomSelect';

const MeetingScheduler = ({ user, onClose, onMeetingCreated }) => {
  const modal = useModal();
  // Get selected org/brand from global context
  const { selectedOrganization: contextOrg, selectedBrand: contextBrand } = useOrgBrand();
  const [step, setStep] = useState(1); // 1: Details & Participants, 2: Select Time, 3: Confirm
  const [loading, setLoading] = useState(false);

  // Check if user is super admin
  const isSuperAdmin = user?.is_super_admin || false;

  // Form data
  const [meetingData, setMeetingData] = useState({
    title: '',
    description: '',
    duration: 60, // minutes
  });

  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [userRole, setUserRole] = useState(null); // Current user's role from API

  const [availableOrganizations, setAvailableOrganizations] = useState([]);
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [availableBrands, setAvailableBrands] = useState([]);
  const [selectedBrand, setSelectedBrand] = useState(null);

  // External guest support
  const [externalGuests, setExternalGuests] = useState([]);
  const [newGuestEmail, setNewGuestEmail] = useState('');

  // Participant picker dropdown
  const [showParticipantDropdown, setShowParticipantDropdown] = useState(false);
  const [participantSearchTerm, setParticipantSearchTerm] = useState('');

  const [dateRange, setDateRange] = useState({
    start: new Date().toISOString().split('T')[0],
    end: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
  });

  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null); // 'YYYY-MM-DD' of the day shown in the time panel
  const [calendarMonth, setCalendarMonth] = useState(() => moment());

  // Fetch available users and organizations for selection
  useEffect(() => {
    fetchAvailableOrganizations();
  }, []);

  // Auto-fill from context when organizations are loaded (but allow user to change)
  useEffect(() => {
    if (availableOrganizations.length > 0 && !selectedOrganization) {
      if (contextOrg) {
        // Find the matching org in available organizations
        const matchingOrg = availableOrganizations.find(o =>
          o.id === contextOrg._id || o._id === contextOrg._id
        );
        if (matchingOrg) {
          setSelectedOrganization(matchingOrg.id || matchingOrg._id);
          fetchBrandsForOrganization(matchingOrg.id || matchingOrg._id);
          return;
        }
      }
      // If no context org or context org not found, select first organization
      if (availableOrganizations.length > 0) {
        const firstOrg = availableOrganizations[0];
        setSelectedOrganization(firstOrg.id || firstOrg._id);
        fetchBrandsForOrganization(firstOrg.id || firstOrg._id);
      }
    }
  }, [availableOrganizations, contextOrg]);

  // Auto-fill brand from context when brands are loaded (but allow user to change)
  useEffect(() => {
    if (availableBrands.length > 0 && !selectedBrand) {
      if (contextBrand) {
        // Try to find the matching brand from context
        const matchingBrand = availableBrands.find(b =>
          (b.id === contextBrand._id || b.id === contextBrand.id) ||
          (b._id === contextBrand._id || b._id === contextBrand.id)
        );
        if (matchingBrand) {
          setSelectedBrand(matchingBrand.id || matchingBrand._id);
          return;
        }
      }
      // If no context brand or context brand not found, select first brand
      if (availableBrands.length > 0) {
        setSelectedBrand(availableBrands[0].id || availableBrands[0]._id);
      }
    }
  }, [availableBrands, contextBrand]);

  // Fetch participants when org/brand selection changes
  useEffect(() => {
    fetchAvailableParticipants();
  }, [selectedOrganization, selectedBrand]);

  // Close the participant picker dropdown when clicking outside it
  useEffect(() => {
    if (!showParticipantDropdown) return;

    const handleClickOutside = (e) => {
      if (!e.target.closest('.participant-dropdown-wrapper')) {
        setShowParticipantDropdown(false);
        setParticipantSearchTerm('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showParticipantDropdown]);

  const fetchAvailableParticipants = async () => {
    try {
      // Build query params for filtering
      const params = new URLSearchParams();
      if (selectedOrganization) {
        params.append('organization_id', selectedOrganization);
      }
      if (selectedBrand) {
        params.append('brand_id', selectedBrand);
      }

      const response = await fetch(`/api/meetings/available-participants?${params.toString()}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableUsers(data.participants || []);
        setUserRole(data.user_role);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch participants:', response.status, errorData);
        modal.error(`Failed to load participants: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error fetching participants:', err);
      modal.error('Failed to load participants. Please try again.');
    }
  };

  const fetchAvailableOrganizations = async () => {
    try {
      const response = await fetch('/api/user/organizations', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setAvailableOrganizations(data.organizations || []);
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch organizations:', response.status, errorData);
        modal.error(`Failed to load organizations: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error fetching organizations:', err);
      modal.error('Failed to load organizations. Please try again.');
    }
  };

  const fetchBrandsForOrganization = async (organizationId) => {
    try {
      const response = await fetch('/api/brands/list', {
        method: 'GET',
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        // Filter brands by selected organization
        const orgBrands = (data.brands || []).filter(b => b.organization_id === organizationId);

        // Sort brands alphabetically by name
        const sortedBrands = orgBrands.sort((a, b) => {
          const nameA = (a.name || '').toLowerCase();
          const nameB = (b.name || '').toLowerCase();
          return nameA.localeCompare(nameB);
        });

        setAvailableBrands(sortedBrands);
        // Brand auto-selection is handled by useEffect
      } else {
        const errorData = await response.json();
        console.error('Failed to fetch brands:', response.status, errorData);
        modal.error(`Failed to load brands: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error fetching brands:', err);
      modal.error('Failed to load brands. Please try again.');
    }
  };

  const handleOrganizationChange = (e) => {
    const orgId = e.target.value;
    setSelectedOrganization(orgId);
    setSelectedParticipants([]); // Reset participants when org changes
    setSelectedBrand(null); // Reset brand so it gets re-derived for the new org
    if (orgId) {
      fetchBrandsForOrganization(orgId); // This will auto-select the first brand
    } else {
      setAvailableBrands([]);
    }
  };

  // External guest handling
  const handleAddExternalGuest = () => {
    const email = newGuestEmail.trim().toLowerCase();
    if (!email) return;

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      modal.error('Please enter a valid email address');
      return;
    }

    // Check if already added
    if (externalGuests.includes(email)) {
      modal.error('This guest has already been added');
      return;
    }

    // Check if this email belongs to an available user
    const existingUser = availableUsers.find(u => u.email.toLowerCase() === email);
    if (existingUser) {
      modal.error('This email belongs to a registered user. Please select them from the participants list.');
      return;
    }

    setExternalGuests(prev => [...prev, email]);
    setNewGuestEmail('');
  };

  const handleRemoveExternalGuest = (email) => {
    setExternalGuests(prev => prev.filter(e => e !== email));
  };

  // Validation for external guests
  const validateExternalGuests = () => {
    if (externalGuests.length === 0) return true;

    // For operators/admins: external guests are allowed without requiring a client
    // For clients: still require at least 1 operator
    const currentUserIsAgent = userRole === 'operator' || userRole === 'admin' || userRole === 'super_admin';

    if (currentUserIsAgent) {
      // Operators can add external guests freely
      return true;
    }

    // For non-operators, require at least 1 operator in the meeting
    const selectedUserObjects = selectedParticipants.map(id => availableUsers.find(u => u.id === id)).filter(Boolean);
    const hasAgent = selectedUserObjects.some(u => u.role === 'operator' || u.role === 'admin');

    return hasAgent;
  };

  const getExternalGuestValidationMessage = () => {
    if (externalGuests.length === 0) return null;

    const currentUserIsAgent = userRole === 'operator' || userRole === 'admin' || userRole === 'super_admin';

    if (currentUserIsAgent) {
      // Operators don't need additional validation
      return null;
    }

    // For non-operators, check if there's at least 1 operator
    const selectedUserObjects = selectedParticipants.map(id => availableUsers.find(u => u.id === id)).filter(Boolean);
    const hasAgent = selectedUserObjects.some(u => u.role === 'operator' || u.role === 'admin');

    if (!hasAgent) {
      return 'External guests require at least 1 operator in the meeting';
    }

    return null;
  };

  // Get role badge color
  const getRoleBadgeClass = (role) => {
    switch (role) {
      case 'admin':
      case 'super_admin':
        return 'role-badge-admin';
      case 'operator':
        return 'role-badge-operator';
      case 'client':
        return 'role-badge-client';
      default:
        return 'role-badge-default';
    }
  };

  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'super_admin':
        return 'Super Admin';
      case 'operator':
        return 'Operator';
      case 'client':
        return 'Client';
      default:
        return role;
    }
  };

  // Order and labels used to group the participant picker by role
  const ROLE_GROUP_ORDER = ['admin', 'super_admin', 'operator', 'client'];
  const ROLE_GROUP_LABELS = {
    admin: 'Admins',
    super_admin: 'Super Admins',
    operator: 'Operators',
    client: 'Clients',
  };

  // Unselected, search-matched users available to pick, grouped by role
  const getGroupedAvailableUsers = () => {
    const term = participantSearchTerm.trim().toLowerCase();
    const pickable = availableUsers.filter(u => {
      if (selectedParticipants.includes(u.id)) return false;
      if (!term) return true;
      return (u.name || '').toLowerCase().includes(term) || (u.email || '').toLowerCase().includes(term);
    });

    const groups = {};
    pickable.forEach(u => {
      const key = ROLE_GROUP_ORDER.includes(u.role) ? u.role : 'other';
      if (!groups[key]) groups[key] = [];
      groups[key].push(u);
    });

    const orderedKeys = [...ROLE_GROUP_ORDER, 'other'];
    return orderedKeys
      .filter(key => groups[key] && groups[key].length > 0)
      .map(key => ({
        key,
        label: ROLE_GROUP_LABELS[key] || 'Other',
        users: groups[key],
      }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Convert duration to number to ensure API receives correct type
    const processedValue = name === 'duration' ? parseInt(value, 10) : value;
    setMeetingData(prev => ({ ...prev, [name]: processedValue }));
  };

  const handleParticipantToggle = (userId) => {
    setSelectedParticipants(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const checkAvailability = async () => {
    // Validate external guests before proceeding
    if (!validateExternalGuests()) {
      modal.error(getExternalGuestValidationMessage());
      return;
    }

    setLoading(true);

    try {
      // Get participant emails - using 'id' not '_id'
      const participantEmails = selectedParticipants.map(userId => {
        const participantUser = availableUsers.find(u => u.id === userId);
        return participantUser?.email;
      }).filter(Boolean);

      // Always include the current user (organizer) in participant emails
      if (user?.email && !participantEmails.includes(user.email)) {
        participantEmails.push(user.email);
      }

      // Note: External guests are NOT included in availability check
      // (they don't have calendar access we can query)

      // Build time range - send full day, backend will filter to business hours
      const timeMin = new Date(dateRange.start + 'T00:00:00').toISOString();
      const timeMax = new Date(dateRange.end + 'T23:59:59').toISOString();

      // Get user's timezone
      const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      const response = await fetch('/api/meetings/available-slots', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          participant_emails: participantEmails,
          time_min: timeMin,
          time_max: timeMax,
          duration_minutes: meetingData.duration,
          slot_interval_minutes: 30,
          timezone: userTimezone,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to check availability');
      }

      const slots = data.slots || [];
      setAvailableSlots(slots);
      setSelectedSlot(null);

      // Jump the calendar to the first day that actually has a slot, and pre-select it
      const firstSlot = slots[0];
      const firstDateKey = firstSlot ? moment(firstSlot.start).format('YYYY-MM-DD') : null;
      setSelectedDate(firstDateKey);
      setCalendarMonth(firstDateKey ? moment(firstDateKey) : moment(dateRange.start));

      setStep(2);
    } catch (err) {
      console.error('Error checking availability:', err);
      modal.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const createMeeting = async () => {
    setLoading(true);

    try {
      if (!selectedSlot) {
        throw new Error('Please select a time slot');
      }

      // Prepare participants data - using 'id' not '_id'
      const participantsData = selectedParticipants.map(userId => {
        const participantUser = availableUsers.find(u => u.id === userId);
        return {
          user_id: userId,
          email: participantUser.email,
          name: participantUser.name || participantUser.email,
          role: participantUser.role,
        };
      });

      // Add external guests
      const externalGuestsData = externalGuests.map(email => ({
        email: email,
        name: email.split('@')[0], // Use email prefix as name
        is_external: true,
      }));

      const response = await fetch('/api/meetings', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          title: meetingData.title,
          description: meetingData.description,
          start_time: selectedSlot.start,
          end_time: selectedSlot.end,
          use_squadcast: true,
          participants: participantsData,
          external_guests: externalGuestsData,
          organization_id: selectedOrganization,
          brand_id: selectedBrand,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create meeting');
      }

      // Success!
      const calendarName = user?.auth_provider === 'Microsoft' ? 'Microsoft Calendar' : 'Google Calendar';
      await modal.alert(
        `Meeting created successfully!\n\nCalendar invitations have been sent to all participants.\nCheck your ${calendarName} for the SquadCast link.`,
        'Meeting Created'
      );

      if (onMeetingCreated) {
        onMeetingCreated(data);
      }

      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error creating meeting:', err);
      modal.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatDateTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
  };

  // Group slots by day (YYYY-MM-DD) so the calendar can look up a day's slots directly
  const groupSlotsByDateKey = (slots) => {
    const grouped = {};
    slots.forEach(slot => {
      const dateKey = moment(slot.start).format('YYYY-MM-DD');
      if (!grouped[dateKey]) {
        grouped[dateKey] = [];
      }
      grouped[dateKey].push(slot);
    });
    return grouped;
  };

  // Build the 6-week grid (Sun-Sat) for the given month
  const buildCalendarWeeks = (monthMoment) => {
    const startOfMonth = monthMoment.clone().startOf('month');
    const endOfMonth = monthMoment.clone().endOf('month');
    const gridStart = startOfMonth.clone().startOf('week');
    const gridEnd = endOfMonth.clone().endOf('week');

    const days = [];
    const cursor = gridStart.clone();
    while (cursor.isSameOrBefore(gridEnd, 'day')) {
      days.push(cursor.clone());
      cursor.add(1, 'day');
    }

    const weeks = [];
    for (let i = 0; i < days.length; i += 7) {
      weeks.push(days.slice(i, i + 7));
    }
    return weeks;
  };

  const renderStep1 = () => (
    <div className="scheduler-step">
      <div className="scheduler-left">
        <h2>Schedule a Meeting</h2>
        <p className="subtitle">Enter meeting details and select participants</p>

        <div className="scheduler-content scheduler-content-step1">
          <div className="scheduler-col scheduler-col-basics">
            <div className="form-section">
              <label>Meeting Title *</label>
              <input
                type="text"
                name="title"
                value={meetingData.title}
                onChange={handleInputChange}
                placeholder="e.g., Client Strategy Call"
                required
                className="input-field"
                autoComplete="off"
                data-1p-ignore="true"
                data-lpignore="true"
                data-bwignore="true"
                data-form-type="other"
              />
            </div>

            <div className="form-section">
              <label>Description</label>
              <textarea
                name="description"
                value={meetingData.description}
                onChange={handleInputChange}
                placeholder="What will you discuss?"
                rows={2}
                className="input-field"
              />
            </div>

            <div className="form-section">
              <label>Duration *</label>
              <CustomSelect
                  name="duration"
                  value={meetingData.duration}
                  onChange={handleInputChange}
                  className="input-field"
                >
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                  <option value={45}>45 minutes</option>
                  <option value={60}>1 hour</option>
                  <option value={90}>1.5 hours</option>
                  <option value={120}>2 hours</option>
                </CustomSelect>
            </div>

            <div className="form-row">
              <div className="form-section">
                <label>Organization *</label>
                <CustomSelect
                    value={selectedOrganization || ''}
                    onChange={handleOrganizationChange}
                    className="input-field"
                    required
                  >
                    {availableOrganizations.map(org => (
                      <option key={org.id} value={org.id}>
                        {org.name}
                      </option>
                    ))}
                  </CustomSelect>
                {availableOrganizations.length === 0 && (
                  <p className="hint-text">No organizations available. Please contact your administrator.</p>
                )}
              </div>

              <div className="form-section">
                <label>Brand *</label>
                <CustomSelect
                    value={selectedBrand || ''}
                    onChange={(e) => setSelectedBrand(e.target.value)}
                    className="input-field"
                    required
                    disabled={!selectedOrganization}
                  >
                    {availableBrands.map(brand => (
                      <option key={brand.id} value={brand.id}>
                        {brand.name}
                      </option>
                    ))}
                  </CustomSelect>
                {selectedOrganization && availableBrands.length === 0 && (
                  <p className="hint-text">No brands available for this organization.</p>
                )}
                {!selectedOrganization && (
                  <p className="hint-text">Please select an organization first.</p>
                )}
              </div>
            </div>

            <div className="form-section">
              <label>Date Range to Search</label>
              <div className="date-range-inputs">
                <input
                  type="date"
                  value={dateRange.start}
                  onChange={(e) => setDateRange(prev => ({ ...prev, start: e.target.value }))}
                  min={new Date().toISOString().split('T')[0]}
                  className="input-field"
                />
                <span className="date-separator">to</span>
                <input
                  type="date"
                  value={dateRange.end}
                  onChange={(e) => setDateRange(prev => ({ ...prev, end: e.target.value }))}
                  min={dateRange.start}
                  className="input-field"
                />
              </div>
            </div>
          </div>

          <div className="scheduler-col scheduler-col-people">
            <div className="form-section form-section-participants">
                <label>Select Participants *</label>

                {availableUsers.length === 0 ? (
                  <div className="no-users-message">
                    <p>No participants available for this organization/brand.</p>
                  </div>
                ) : (
                  <div className="participant-dropdown-wrapper">
                    <button
                      type="button"
                      className={`assignment-button${showParticipantDropdown ? ' open' : ''}`}
                      onClick={() => setShowParticipantDropdown(prev => !prev)}
                    >
                      <span className="assignment-button-label">
                        {selectedParticipants.length > 0
                          ? `${selectedParticipants.length} participant${selectedParticipants.length !== 1 ? 's' : ''} selected`
                          : 'Select Participants'}
                      </span>
                      <Icon name="chevron-down" size="0.9rem" className="assignment-button-arrow" />
                    </button>

                    {showParticipantDropdown && (
                      <div className="user-assignment-dropdown">
                        <div className="user-assignment-search">
                          <Icon name="search" size="1rem" />
                          <input
                            type="text"
                            placeholder="Search participants..."
                            value={participantSearchTerm}
                            onChange={(e) => setParticipantSearchTerm(e.target.value)}
                            autoFocus
                            autoComplete="off"
                            data-1p-ignore="true"
                            data-lpignore="true"
                            data-bwignore="true"
                            data-form-type="other"
                          />
                        </div>
                        <div className="user-assignment-list">
                          {getGroupedAvailableUsers().length === 0 ? (
                            <div className="no-users-available">
                              {participantSearchTerm.trim() ? 'No matches found' : 'Everyone available has been selected'}
                            </div>
                          ) : (
                            getGroupedAvailableUsers().map(group => (
                              <div key={group.key} className="user-assignment-group">
                                <div className="user-assignment-group-label">{group.label}</div>
                                {group.users.map(availableUser => (
                                  <div
                                    key={availableUser.id}
                                    className="user-assignment-item"
                                    onClick={() => handleParticipantToggle(availableUser.id)}
                                  >
                                    <Avatar user={availableUser} size="sm" className="user-assignment-avatar" />
                                    <div className="user-assignment-info">
                                      <div className="user-assignment-name">{availableUser.name || availableUser.email}</div>
                                      <div className="user-assignment-email">{availableUser.email}</div>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* External Guests Section */}
              <div className="form-section">
                <label>External Guests (Optional)</label>
                <p className="hint-text">
                  Add guests outside your organization. Operators can schedule with external guests only. Other roles require an operator in the meeting.
                </p>

                <div className="external-guest-input">
                  <input
                    type="email"
                    value={newGuestEmail}
                    onChange={(e) => setNewGuestEmail(e.target.value)}
                    placeholder="guest@example.com"
                    className="input-field"
                    onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddExternalGuest())}
                    autoComplete="off"
                    data-1p-ignore="true"
                    data-lpignore="true"
                    data-bwignore="true"
                    data-form-type="other"
                  />
                  <button
                    type="button"
                    onClick={handleAddExternalGuest}
                    className="btn-add-guest"
                    disabled={!newGuestEmail.trim()}
                  >
                    + Add
                  </button>
                </div>

                {getExternalGuestValidationMessage() && (
                  <div className="external-guest-warning">
                    <Icon name="warning" size="1rem" /> {getExternalGuestValidationMessage()}
                  </div>
                )}
              </div>

            {/* Selected participants + external guests, shown the same way */}
            {(selectedParticipants.length > 0 || externalGuests.length > 0) && (
              <div className="form-section">
                <label>Selected ({selectedParticipants.length + externalGuests.length})</label>
                <div className="assigned-users-list">
                  {selectedParticipants.map(userId => {
                    const participant = availableUsers.find(u => u.id === userId);
                    if (!participant) return null;
                    return (
                      <div key={userId} className="assigned-user-card">
                        <div className="assigned-user-top">
                          <Avatar user={participant} size="sm" />
                          <div className="assigned-user-details">
                            <div className="assigned-user-name-line">
                              <span className="assigned-user-name">{participant.name || participant.email}</span>
                              <span className={`role-badge-small ${getRoleBadgeClass(participant.role)}`}>
                                {getRoleDisplayName(participant.role)}
                              </span>
                              {participant.owner_type === 'org_owner' && (
                                <span className="owner-badge-small owner-badge-org-owner">
                                  <Icon name="building" size="0.65rem" />
                                  Org Owner
                                </span>
                              )}
                              {participant.owner_type === 'brand_owner' && (
                                <span className="owner-badge-small owner-badge-brand-owner">
                                  <Icon name="star" size="0.65rem" />
                                  Brand Owner
                                </span>
                              )}
                            </div>
                            <span className="assigned-user-email">{participant.email}</span>
                          </div>
                          <button
                            type="button"
                            className="assigned-user-remove-btn"
                            onClick={() => handleParticipantToggle(userId)}
                            title="Remove"
                          >
                            <Icon name="close-x" size="0.9rem" />
                          </button>
                        </div>
                      </div>
                    );
                  })}

                  {externalGuests.map(email => (
                    <div key={email} className="assigned-user-card">
                      <div className="assigned-user-top">
                        <Avatar user={{ email }} size="sm" />
                        <div className="assigned-user-details">
                          <div className="assigned-user-name-line">
                            <span className="assigned-user-name">{email}</span>
                            <span className="role-badge-small role-badge-external">External</span>
                          </div>
                          <span className="assigned-user-email">Guest</span>
                        </div>
                        <button
                          type="button"
                          className="assigned-user-remove-btn"
                          onClick={() => handleRemoveExternalGuest(email)}
                          title="Remove"
                        >
                          <Icon name="close-x" size="0.9rem" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="action-buttons action-buttons-end">
          <button
            onClick={checkAvailability}
            className="btn-primary"
            disabled={
              !meetingData.title ||
              !selectedOrganization ||
              !selectedBrand ||
              (selectedParticipants.length === 0 && externalGuests.length === 0) ||
              !validateExternalGuests() ||
              loading
            }
          >
            {loading ? 'Checking Availability...' : 'Find Available Times →'}
          </button>
        </div>
      </div>
    </div>
  );

  const renderStep2 = () => {
    const slotsByDate = groupSlotsByDateKey(availableSlots);
    const weeks = buildCalendarWeeks(calendarMonth);
    const daySlots = selectedDate ? (slotsByDate[selectedDate] || []) : [];
    const today = moment().format('YYYY-MM-DD');

    return (
      <div className="scheduler-step">
        <div className="scheduler-left">
          <h2>Select a Time</h2>
          <p className="subtitle">
            Found {availableSlots.length} available time slot{availableSlots.length !== 1 ? 's' : ''} when everyone is free
          </p>

          {availableSlots.length === 0 ? (
            <div className="no-slots-message">
              <div className="icon"><Icon name="calendar" size="1.5rem" /></div>
              <h3>No available times found</h3>
              <p>Try expanding your date range or selecting fewer participants.</p>
              <button onClick={() => setStep(1)} className="btn-primary">
                ← Adjust Search Criteria
              </button>
            </div>
          ) : (
            <>
              <div className="scheduler-content scheduler-content-step2">
                <div className="scheduler-col scheduler-col-calendar">
                  <div className="time-calendar">
                    <div className="time-calendar-header">
                      <button
                        type="button"
                        className="time-calendar-nav"
                        onClick={() => setCalendarMonth(prev => prev.clone().subtract(1, 'month'))}
                      >
                        ‹
                      </button>
                      <span className="time-calendar-month">{calendarMonth.format('MMMM YYYY')}</span>
                      <button
                        type="button"
                        className="time-calendar-nav"
                        onClick={() => setCalendarMonth(prev => prev.clone().add(1, 'month'))}
                      >
                        ›
                      </button>
                    </div>

                    <div className="time-calendar-weekdays">
                      {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                        <div key={i}>{d}</div>
                      ))}
                    </div>

                    <div className="time-calendar-grid">
                      {weeks.flat().map(day => {
                        const dateKey = day.format('YYYY-MM-DD');
                        const hasSlots = !!slotsByDate[dateKey];
                        const inMonth = day.month() === calendarMonth.month();
                        const isSelected = dateKey === selectedDate;
                        const isToday = dateKey === today;

                        return (
                          <button
                            type="button"
                            key={dateKey}
                            className={[
                              'time-calendar-day',
                              !inMonth && 'outside',
                              hasSlots && 'has-slots',
                              isSelected && 'selected',
                              isToday && 'today',
                            ].filter(Boolean).join(' ')}
                            disabled={!hasSlots}
                            onClick={() => setSelectedDate(dateKey)}
                          >
                            {day.date()}
                            {hasSlots && <span className="time-calendar-dot" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <div className="scheduler-col scheduler-col-day">
                  <div className="time-day-panel">
                    {selectedDate ? (
                      <>
                        <h3 className="time-day-header">{moment(selectedDate).format('dddd, MMMM D, YYYY')}</h3>
                        <p className="hint-text">
                          {daySlots.length} time{daySlots.length !== 1 ? 's' : ''} available · {meetingData.duration} min meeting
                        </p>
                        <div className="time-slots-grid">
                          {daySlots.map((slot, index) => (
                            <button
                              key={index}
                              className={`time-slot-button ${selectedSlot === slot ? 'selected' : ''}`}
                              onClick={() => setSelectedSlot(slot)}
                            >
                              <span className="time-text">{formatTime(slot.start)}</span>
                              {selectedSlot === slot && <span className="check-icon">✓</span>}
                            </button>
                          ))}
                        </div>
                      </>
                    ) : (
                      <p className="hint-text">Select a highlighted date on the calendar to see available times.</p>
                    )}
                  </div>
                </div>
              </div>

              <div className="action-buttons">
                <button onClick={() => setStep(1)} className="btn-secondary">
                  ← Back
                </button>
                <button
                  onClick={() => setStep(3)}
                  className="btn-primary"
                  disabled={!selectedSlot}
                >
                  Continue to Confirm →
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderStep3 = () => (
    <div className="scheduler-step">
      <div className="scheduler-left">
        <h2>Confirm Meeting</h2>
        <p className="subtitle">Review the details before creating your meeting</p>

        <div className="confirmation-card">
          <div className="confirmation-row">
            <strong>Title:</strong>
            <span>{meetingData.title}</span>
          </div>

          {meetingData.description && (
            <div className="confirmation-row">
              <strong>Description:</strong>
              <span>{meetingData.description}</span>
            </div>
          )}

          <div className="confirmation-row">
            <strong>Date & Time:</strong>
            <span>
              {selectedSlot && formatDateTime(selectedSlot.start)}
            </span>
          </div>

          <div className="confirmation-row">
            <strong>Duration:</strong>
            <span>{meetingData.duration} minutes</span>
          </div>

          <div className="confirmation-row">
            <strong>Participants:</strong>
            <div className="participants-list">
              <div className="participant-item-small">
                <Avatar user={user} size="sm" className="participant-avatar-small" />
                {user.name} (you)
                <span className={`role-badge-small ${getRoleBadgeClass(userRole)}`}>
                  {getRoleDisplayName(userRole)}
                </span>
              </div>
              {selectedParticipants.map(userId => {
                const participant = availableUsers.find(u => u.id === userId);
                return (
                  <div key={userId} className="participant-item-small">
                    <Avatar user={participant} size="sm" className="participant-avatar-small" />
                    {participant?.name || participant?.email}
                    <span className={`role-badge-small ${getRoleBadgeClass(participant?.role)}`}>
                      {getRoleDisplayName(participant?.role)}
                    </span>
                    {participant?.owner_type === 'org_owner' && (
                      <span className="owner-badge-small owner-badge-org-owner">
                        <Icon name="building" size="0.65rem" />
                        ORG OWNER
                      </span>
                    )}
                    {participant?.owner_type === 'brand_owner' && (
                      <span className="owner-badge-small owner-badge-brand-owner">
                        <Icon name="star" size="0.65rem" />
                        BRAND OWNER
                      </span>
                    )}
                  </div>
                );
              })}
              {externalGuests.map(email => (
                <div key={email} className="participant-item-small external">
                  <span className="participant-initial external-icon"><Icon name="paper-plane-empty" size="1rem" /></span>
                  {email}
                  <span className="role-badge-small role-badge-external">EXTERNAL</span>
                </div>
              ))}
            </div>
          </div>

          <div className="confirmation-info-box">
            <div className="info-icon"><Icon name="play" size="1.5rem" /></div>
            <div>
              <strong>SquadCast Recording Included</strong>
              <p>A SquadCast recording session will be automatically created and the link will be included in the calendar invitation sent to all participants.</p>

              <div style={{ marginTop: '1.5rem' }}>
                <strong>What happens after creation:</strong>
                <ul style={{ marginTop: '0.5rem', paddingLeft: '1.2rem', listStyle: 'disc' }}>
                  <li>Calendar invites sent to all participants</li>
                  <li>SquadCast link included in invite</li>
                  <li>Email notifications sent</li>
                  <li>Events added to each participant's calendar</li>
                  <li>Meeting details saved to your account</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button onClick={() => setStep(2)} className="btn-secondary">
            ← Back
          </button>
          <button
            onClick={createMeeting}
            className="btn-primary btn-success"
            disabled={loading}
          >
            {loading ? 'Creating Meeting...' : 'Create Meeting with SquadCast'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="meeting-scheduler-container">
      <div className="scheduler-header">
        <div className="step-progress">
          <div className={`progress-step ${step >= 1 ? 'active' : ''} ${step > 1 ? 'completed' : ''}`}>
            <div className="step-number">{step > 1 ? '✓' : '1'}</div>
            <div className="step-label">Details</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 2 ? 'active' : ''} ${step > 2 ? 'completed' : ''}`}>
            <div className="step-number">{step > 2 ? '✓' : '2'}</div>
            <div className="step-label">Select Time</div>
          </div>
          <div className="progress-line"></div>
          <div className={`progress-step ${step >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Confirm</div>
          </div>
        </div>
      </div>

      <div className="scheduler-body">
        {step === 1 && renderStep1()}
        {step === 2 && renderStep2()}
        {step === 3 && renderStep3()}
      </div>
    </div>
  );
};

export default MeetingScheduler;
